/**
 * Claim hooks (CC-Claims) — house list stack + store-first mutations.
 *
 * Co-located with the claims module (the slice owns `src/components/claims/**`,
 * not `src/hooks/`); the contract matches useSalesOrders.ts exactly:
 * - `useClaimsTable` drives the rebuilt list via useAntdTable with the
 *   pre-first-fetch `loading || params.length === 0` guard and a store echo.
 *   side / status / search go server-side; the `type` filter unions client-side
 *   over a mock-scale fetch so pagination totals stay correct (the same pattern
 *   as useSalesOrdersTable's multi-customer union, and it avoids extending the
 *   salvaged `ClaimListParams` contract, which this slice does not own).
 * - `useClaim` reads the detail with an `/api/`-prefixed tracked cacheKey.
 * - mutations write the Zustand echo first, then invalidate via exact-key
 *   clears (the ahooks `clearCache(predicate)` form is a silent no-op).
 */

import { clearCache, useAntdTable, useRequest } from 'ahooks';
import { claimApi } from '@/services/api';
import { clearCacheWhere, trackCacheKey } from '@/utils/cacheKeyRegistry';
import type {
  Claim,
  ClaimListParams,
  CreateClaimRequest,
  TransitionClaimRequest,
} from '@/types/api/claim';
import { useClaimStore } from './claimStore';
import type { ClaimFilters } from './claimStore';

// ---------------------------------------------------------------------------
// Cache keys (static, /api/-prefixed — house rule)
// ---------------------------------------------------------------------------

export const claimDetailCacheKey = (id: string) => `/api/claims/${id}`;

/** Invalidate every cache a claim mutation can stale (exact keys only). */
export function invalidateClaimCaches(...claimIds: Array<string | undefined>) {
  const detailKeys = claimIds
    .filter((id): id is string => Boolean(id))
    .map((id) => claimDetailCacheKey(id));
  if (detailKeys.length > 0) clearCache(detailKeys);
  clearCacheWhere((key) => key.startsWith('/api/claims'));
}

// ---------------------------------------------------------------------------
// List (useAntdTable — S0.3 list-kit contract)
// ---------------------------------------------------------------------------

/** Mock-scale ceiling for the client-side union fetch (type filter). */
const UNION_FETCH_PAGE_SIZE = 500;

export function useClaimsTable(filters: ClaimFilters) {
  const { setClaims, setPagination } = useClaimStore();

  const needsClientFilter = filters.type !== 'all';

  const baseParams = (): ClaimListParams => ({
    search: filters.search.trim() || undefined,
    side: filters.side === 'all' ? undefined : filters.side,
    status: filters.status === 'all' ? undefined : filters.status,
  });

  const { tableProps, refresh, loading, error, params } = useAntdTable(
    async ({ current, pageSize }) => {
      if (!needsClientFilter) {
        const response = await claimApi.list({ ...baseParams(), page: current, pageSize });
        setClaims(response.data);
        setPagination(response.pagination);
        return { total: response.pagination.total, list: response.data };
      }

      // Type bucket: union client-side over a mock-scale fetch so pagination
      // totals stay correct (ClaimListParams has no `type` field to send).
      const response = await claimApi.list({
        ...baseParams(),
        page: 1,
        pageSize: UNION_FETCH_PAGE_SIZE,
      });
      const rows = response.data.filter((claim) => claim.type === filters.type);
      const start = (current - 1) * pageSize;
      const pageRows = rows.slice(start, start + pageSize);
      setClaims(pageRows);
      setPagination({
        page: current,
        pageSize,
        total: rows.length,
        totalPages: Math.ceil(rows.length / pageSize) || 1,
      });
      return { total: rows.length, list: pageRows };
    },
    {
      defaultPageSize: 10,
      refreshDeps: [filters.search, filters.side, filters.status, filters.type],
    }
  );

  return {
    tableProps,
    refresh,
    // Pre-first-fetch guard (house rule) — never flash the empty state.
    isLoading: loading || params.length === 0,
    error,
  };
}

// ---------------------------------------------------------------------------
// Detail
// ---------------------------------------------------------------------------

export function useClaim(id?: string) {
  const { data, error, loading, run } = useRequest(() => claimApi.getById(id!), {
    ready: !!id,
    refreshDeps: [id],
    cacheKey: id ? trackCacheKey(claimDetailCacheKey(id)) : undefined,
  });

  return {
    claim: data?.data,
    error,
    isLoading: loading,
    /** Invalidate + re-run (never bare ahooks refresh in components). */
    refresh: () => {
      if (id) invalidateClaimCaches(id);
      return run();
    },
  };
}

// ---------------------------------------------------------------------------
// Mutations (store-first: update the Zustand echo, then exact-key clearCache)
// ---------------------------------------------------------------------------

export function useClaimMutations() {
  const { upsertClaimInList } = useClaimStore();

  const createClaim = async (body: CreateClaimRequest): Promise<Claim> => {
    const response = await claimApi.create(body);
    upsertClaimInList(response.data);
    invalidateClaimCaches(response.data.id);
    return response.data;
  };

  const transitionClaim = async (id: string, body: TransitionClaimRequest): Promise<Claim> => {
    const response = await claimApi.transition(id, body);
    upsertClaimInList(response.data);
    invalidateClaimCaches(id);
    return response.data;
  };

  return { createClaim, transitionClaim };
}
